import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  type DocumentRow,
  type FinanceSummary,
  formatDate,
  type PartyRef,
  type Payment,
  PAYMENT_METHOD_LABEL,
  RECEIPT_STATUS_LABEL,
} from '@reikraft/shared';
import { type Column, Dot, ErrorBox, Field, Icon, Kpi, Kpis, Loader, Meter, Modal, Panel, Table, Tabs, useToast } from '@reikraft/ui';
import { api, errorText } from '../api';
import { Hint, PageHead } from '../components/common';
import { PaymentModal } from '../components/PaymentModal';
import { formatMoney, partyHref } from '../format';
import { useUrlState } from '../hooks';
import { useFlag, useMe } from '../session';

const RECEIPT_COLOR = {
  pending: 'var(--warn)',
  issued: 'var(--success)',
  cancelled: 'var(--muted-2)',
  not_required: 'var(--line-3)',
} as const;

export function FinancePage() {
  const npd = useFlag('npd');
  const documents = useFlag('documents');
  const me = useMe();
  const [state, set] = useUrlState({ tab: npd ? 'receipts' : 'payments' });
  const [pay, setPay] = useState<{ party: PartyRef | null; amount?: number } | null>(null);
  const [receipt, setReceipt] = useState<Payment | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const summary = useQuery({ queryKey: ['finance', 'summary'], queryFn: () => api.get<FinanceSummary>('/admin/finance/summary') });
  const payments = useQuery({
    queryKey: ['payments', state.tab],
    queryFn: () => api.get<Payment[]>(`/admin/payments${state.tab === 'receipts' ? '?receipt=pending' : ''}`),
    enabled: state.tab === 'payments' || state.tab === 'receipts',
  });
  const docs = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.get<DocumentRow[]>('/admin/documents'),
    enabled: documents && state.tab === 'documents',
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/payments/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['finance'] });
      toast('Оплата удалена');
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const s = summary.data;
  const debtTotal = (s?.debts ?? []).reduce((a, d) => a + d.debtKop, 0);

  const payCols: Column<Payment>[] = [
    { key: 'date', title: 'Дата', width: '96px', render: (p) => <span className="rk-mono">{formatDate(p.paidAt)}</span> },
    {
      key: 'party',
      title: 'От кого',
      width: 'minmax(160px, 1.2fr)',
      render: (p) => (
        <span className="rk-td--stack">
          {p.party ? <Link to={partyHref(p.party)}>{p.party.name}</Link> : '—'}
          <span className="rk-td__mono-sub">{p.allocations.map((a) => a.number).join(', ') || 'аванс'}</span>
        </span>
      ),
    },
    { key: 'sum', title: 'Сумма', width: '120px', align: 'right', render: (p) => <b className="rk-mono">{formatMoney(p.amountKop)}</b> },
    { key: 'method', title: 'Способ', width: '100px', render: (p) => PAYMENT_METHOD_LABEL[p.method] },
    { key: 'by', title: 'Получил', width: '130px', render: (p) => <span className="rk-text-2">{p.receivedBy?.name ?? '—'}</span> },
    ...(npd
      ? [
          {
            key: 'receipt',
            title: 'Чек',
            width: '200px',
            render: (p: Payment) => (
              <span className="rk-td--stack">
                <span className="rk-row" style={{ ['--rk-gap' as string]: '6px', fontSize: 13 }}>
                  <Dot color={p.receipt.overdue ? 'var(--danger)' : RECEIPT_COLOR[p.receipt.status]} size="sm" />
                  {p.receipt.status === 'issued' && p.receipt.url ? (
                    <a href={p.receipt.url} target="_blank" rel="noreferrer" className="rk-accent">
                      {RECEIPT_STATUS_LABEL[p.receipt.status]}
                    </a>
                  ) : (
                    RECEIPT_STATUS_LABEL[p.receipt.status]
                  )}
                </span>
                {p.receipt.status === 'pending' && (
                  <span className="rk-td__mono-sub" style={{ color: p.receipt.overdue ? 'var(--danger-text)' : undefined }}>
                    до {formatDate(p.receipt.dueDate)}
                    {p.payerInn ? ` · ИНН ${p.payerInn}` : ''}
                  </span>
                )}
              </span>
            ),
          },
        ]
      : []),
    {
      key: 'act',
      title: '',
      width: '120px',
      render: (p) => (
        <span className="rk-row" style={{ ['--rk-gap' as string]: '6px' }}>
          {npd && p.receipt.status === 'pending' && (
            <button type="button" className="rk-btn rk-btn--sm" onClick={() => setReceipt(p)}>
              Чек выбит
            </button>
          )}
          {me.role === 'admin' && state.tab === 'payments' && (
            <button
              type="button"
              className="rk-btn rk-btn--sm rk-btn--icon"
              aria-label="Удалить оплату"
              title="Удалить"
              onClick={() => window.confirm(`Удалить оплату ${formatMoney(p.amountKop)}?`) && remove.mutate(p.id)}
            >
              ×
            </button>
          )}
        </span>
      ),
    },
  ];

  const tabs = [
    ...(npd ? [{ id: 'receipts', label: 'Чеки к выбиванию', count: s?.receiptsPending ?? 0 }] : []),
    { id: 'payments', label: 'Оплаты' },
    { id: 'debts', label: 'Долги', count: s?.debts.length ?? 0 },
    ...(documents ? [{ id: 'documents', label: 'Счета' }] : []),
  ];

  return (
    <>
      <PageHead
        title="Финансы"
        actions={
          <>
            {npd && (
              <a className="rk-btn" href="https://lknpd.nalog.ru/" target="_blank" rel="noreferrer">
                «Мой налог» <Icon name="external" size={14} />
              </a>
            )}
            <button type="button" className="rk-btn rk-btn--primary" onClick={() => setPay({ party: null })}>
              <Icon name="plus" size={16} /> Принять оплату
            </button>
          </>
        }
      />
      {summary.isError && <ErrorBox error={summary.error} />}
      {s && (
        <Kpis min={170}>
          <Kpi label={`Поступления · ${s.monthName}`} value={formatMoney(s.monthRevenueKop)} />
          {npd && (
            <Kpi
              label="Чеки к выбиванию"
              value={s.receiptsPending}
              tone={s.receiptsOverdue ? 'danger' : s.receiptsPending ? 'accent' : undefined}
              note={s.receiptsOverdue ? `просрочено: ${s.receiptsOverdue}` : undefined}
            />
          )}
          <Kpi label="Долги клиентов" value={formatMoney(debtTotal)} />
          {npd &&
            (s.npd.recipients.length ? s.npd.recipients : [{ userId: '-', name: me.name, incomeKop: 0, percent: 0 }]).map((r) => (
              <Kpi
                key={r.userId}
                label={`Доход ${s.year} · ${r.name}`}
                value={formatMoney(r.incomeKop)}
                note={`${r.percent}% лимита НПД ${formatMoney(s.npd.limitKop)}`}
              >
                <Meter value={r.incomeKop} max={s.npd.limitKop} tone={r.percent >= 100 ? 'danger' : r.percent >= 80 ? 'warn' : undefined} />
              </Kpi>
            ))}
        </Kpis>
      )}

      <Tabs items={tabs} value={state.tab} onChange={(tab) => set({ tab })} label="Разделы финансов" />

      {(state.tab === 'payments' || state.tab === 'receipts') &&
        (payments.isLoading ? (
          <Loader />
        ) : payments.isError ? (
          <ErrorBox error={payments.error} />
        ) : (
          <>
            {state.tab === 'receipts' && (
              <Hint>
                Самозанятый выбивает чек на каждое поступление: за перевод — не позднее 9-го числа следующего месяца, за наличные и карту —
                сразу. Для организации и ИП в чеке указывается их ИНН. Выбейте чек в «Мой налог» и отметьте его здесь со ссылкой на чек.
              </Hint>
            )}
            <Table
              label="Оплаты"
              columns={payCols}
              rows={payments.data!}
              rowKey={(p) => p.id}
              minWidth={940}
              empty={state.tab === 'receipts' ? 'Все чеки выбиты' : 'Оплат пока нет'}
            />
          </>
        ))}

      {state.tab === 'debts' &&
        (s ? (
          <Panel bodyless>
            <div className="rk-rows">
              {s.debts.map((d) => (
                <div key={d.party.id} className="rk-row" style={{ padding: '12px 18px', justifyContent: 'space-between' }}>
                  <span className="rk-td--stack">
                    <Link to={partyHref(d.party)}>
                      <b>{d.party.name}</b>
                    </Link>
                    <span className="rk-muted" style={{ fontSize: 12 }}>
                      Неоплаченных заказов: {d.orders} ·{' '}
                      {s.unpaidOrders
                        .filter((u) => u.party?.id === d.party.id)
                        .map((u) => u.number)
                        .join(', ')}
                    </span>
                  </span>
                  <span className="rk-row" style={{ ['--rk-gap' as string]: '12px' }}>
                    <b className="rk-mono" style={{ color: 'var(--danger-text)' }}>
                      {formatMoney(d.debtKop)}
                    </b>
                    <button type="button" className="rk-btn rk-btn--sm" onClick={() => setPay({ party: d.party, amount: d.debtKop })}>
                      Принять оплату
                    </button>
                  </span>
                </div>
              ))}
              {!s.debts.length && <div className="rk-empty">Долгов нет</div>}
            </div>
          </Panel>
        ) : (
          <Loader />
        ))}

      {state.tab === 'documents' && documents && (docs.isLoading ? <Loader /> : <DocumentsTable docs={docs.data ?? []} />)}

      <PaymentModal open={!!pay} onClose={() => setPay(null)} party={pay?.party ?? null} defaultAmountKop={pay?.amount} />
      <ReceiptModal payment={receipt} onClose={() => setReceipt(null)} />
    </>
  );
}

function DocumentsTable({ docs }: { docs: DocumentRow[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const status = useMutation({
    mutationFn: ({ id, s }: { id: string; s: string }) => api.patch(`/admin/documents/${id}`, { status: s }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const cols: Column<DocumentRow>[] = [
    {
      key: 'n',
      title: 'Счёт',
      width: '140px',
      render: (d) => (
        <a href={d.url} target="_blank" rel="noreferrer" className="rk-mono rk-accent">
          {d.number}
        </a>
      ),
    },
    { key: 'date', title: 'Дата', width: '100px', render: (d) => <span className="rk-mono">{formatDate(d.date)}</span> },
    { key: 'org', title: 'Организация', width: 'minmax(160px, 1fr)', render: (d) => d.organization?.name ?? '—' },
    {
      key: 'orders',
      title: 'Заказы',
      width: 'minmax(140px, 1fr)',
      render: (d) => (
        <span className="rk-mono rk-text-2" style={{ fontSize: 12 }}>
          {d.orders.map((o) => o.number).join(', ')}
        </span>
      ),
    },
    { key: 'sum', title: 'Сумма', width: '120px', align: 'right', render: (d) => <b className="rk-mono">{formatMoney(d.totalKop)}</b> },
    {
      key: 'st',
      title: 'Статус',
      width: '150px',
      render: (d) => (
        <select
          className="rk-select"
          style={{ height: 36 }}
          value={d.status}
          onChange={(e) => status.mutate({ id: d.id, s: e.target.value })}
          aria-label={`Статус счёта ${d.number}`}
        >
          <option value="issued">Выставлен</option>
          <option value="paid">Оплачен</option>
          <option value="cancelled">Аннулирован</option>
        </select>
      ),
    },
  ];
  return (
    <>
      <Hint>Счёт выставляется из карточки организации — по её неоплаченным заказам.</Hint>
      <Table label="Счета" columns={cols} rows={docs} rowKey={(d) => d.id} minWidth={820} empty="Счетов пока нет" />
    </>
  );
}

function ReceiptModal({ payment, onClose }: { payment: Payment | null; onClose: () => void }) {
  const [url, setUrl] = useState('');
  const [number, setNumber] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  useEffect(() => {
    setUrl(payment?.receipt.url ?? '');
    setNumber(payment?.receipt.number ?? '');
  }, [payment]);
  const save = useMutation({
    mutationFn: (status: 'issued' | 'not_required') =>
      api.patch(`/admin/payments/${payment!.id}/receipt`, { status, url: url || null, number: number || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['finance'] });
      void qc.invalidateQueries({ queryKey: ['counters'] });
      toast('Чек отмечен');
      onClose();
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });
  return (
    <Modal
      open={!!payment}
      onClose={onClose}
      title="Чек «Мой налог»"
      footer={
        <>
          <button type="button" className="rk-btn" style={{ marginRight: 'auto' }} onClick={() => save.mutate('not_required')}>
            Чек не нужен
          </button>
          <button type="button" className="rk-btn rk-btn--primary" disabled={save.isPending} onClick={() => save.mutate('issued')}>
            Чек выбит
          </button>
        </>
      }
    >
      {payment && (
        <div className="rk-stack" style={{ ['--rk-gap' as string]: '4px' }}>
          <div>
            <b>{formatMoney(payment.amountKop)}</b> от {payment.party?.name ?? '—'} · {formatDate(payment.paidAt)}
          </div>
          {payment.payerInn && <div className="rk-mono">ИНН покупателя: {payment.payerInn}</div>}
          <div className="rk-muted">
            Услуга: ремонт рулевой рейки
            {payment.allocations.length ? ` (заказ ${payment.allocations.map((a) => a.number).join(', ')})` : ''}
          </div>
        </div>
      )}
      <Field label="Ссылка на чек" hint="Скопируйте из «Мой налог» → «Чеки» → «Поделиться»">
        <input
          className="rk-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://lknpd.nalog.ru/api/v1/receipt/…"
        />
      </Field>
      <Field label="Номер чека">
        <input className="rk-input rk-input--mono" value={number} onChange={(e) => setNumber(e.target.value)} />
      </Field>
    </Modal>
  );
}
